/* eslint-disable max-len */

import checkBatteryLevel from '~/context/DeviceCheckContext/checks/checkBatteryLevel';
import checkDeviceLockdown from '~/context/DeviceCheckContext/checks/checkDeviceLockdown';
import checkExternalStorage from '~/context/DeviceCheckContext/checks/checkExternalStorage';
import checkMinOsVersion from '~/context/DeviceCheckContext/checks/checkMinOsVersion';
import checkMinScreenSize from '~/context/DeviceCheckContext/checks/checkMinScreenSize';
import checkMinUnityVersion from '~/context/DeviceCheckContext/checks/checkMinUnityVersion';
import checkMultipleMonitors from '~/context/DeviceCheckContext/checks/checkMultipleMonitors';
import checkOpenApps from '~/context/DeviceCheckContext/checks/checkOpenApps';
import checkSecureNetwork from '~/context/DeviceCheckContext/checks/checkSecureNetwork';
import checkUrls from '~/context/DeviceCheckContext/checks/checkUrls';
import { DeviceCheckStage } from '~/generated/graphql';
import { DeviceCheckMetadata } from '~/types';

import NAMES from './NAMES';

export const DEVICE_CHECKS_THRESHOLD = 8 * 1000;
export const DEVICE_CHECKS_TIMEOUT = 0.5 * 1000;

export const DEVICE_CHECKS: Record<DeviceCheckStage, DeviceCheckMetadata> = {
  EXTERNAL_APPLICATIONS_PROHIBITED: {
    check: checkOpenApps,
    failure: () => ({
      description: `Sign out of ${NAMES.APP}, close other applications, and retry.`,
      imgSrc: '/static/img/device-checks/open-apps.svg',
      title: 'Unauthorized applications detected',
    }),
  },
  EXTERNAL_STORAGE_PROHIBITED: {
    check: checkExternalStorage,
    failure: () => ({
      description: 'External media detected. Remove the device and retry.',
      imgSrc: '/static/img/device-checks/external-storage.png',
      title: 'External storage device detected',
    }),
  },
  MEASURED_BATTERY_LEVEL: {
    check: checkBatteryLevel,
    failure: ({ expectedValue }) => ({
      description: `${expectedValue}% battery level required. Connect the charger and retry.`,
      imgSrc: '/static/img/device-checks/low-battery.png',
      title: 'Battery is below the minimum level',
    }),
  },
  MIN_SCREEN_SIZE: {
    check: checkMinScreenSize,
    failure: ({ expectedValue }) => ({
      description: `${NAMES.APP} requires a minimum screen size of ${expectedValue} inches.`,
      imgSrc: '/static/img/device-checks/small-screen.png',
      title: 'Screen size is below minimum level',
    }),
  },
  MINIMUM_OS_VERSION: {
    check: checkMinOsVersion,
    failure: ({ expectedValue }) => ({
      description: `The minimum OS version required is ${expectedValue}. Update to the latest operating system version and retry.`,
      imgSrc: '/static/img/device-checks/version-outdated.png',
      title: 'OS version is outdated',
    }),
  },
  MINIMUM_UNITY_VERSION: {
    check: checkMinUnityVersion,
    failure: () => ({
      description: `Sign out of ${NAMES.APP}, update to the latest version, and retry.`,
      imgSrc: '/static/img/device-checks/version-outdated.png',
      title: `${NAMES.APP} version is outdated`,
    }),
  },
  MULTIPLE_MONITORS_PROHIBITED: {
    check: checkMultipleMonitors,
    failure: () => ({
      description: 'Disconnect the display and retry.',
      imgSrc: '/static/img/device-checks/external-display.png',
      title: 'External display detected',
    }),
  },
  REQUIRED_ACT_ENDPOINT: {
    check: checkUrls,
    failure: () => ({
      description:
        'Unable to reach the required ACT servers. Check your internet connection and retry.',
      imgSrc: '/static/img/device-checks/unreachable-endpoint.png',
      title: 'ACT endpoint unreachable',
    }),
  },
  REQUIRED_PRODUCT_ENDPOINT: {
    check: checkUrls,
    failure: () => ({
      description:
        'Unable to reach the required assessment servers. Check your internet connection and retry.',
      imgSrc: '/static/img/device-checks/unreachable-endpoint.png',
      title: 'Assessment endpoint unreachable',
    }),
  },
  REQUIRES_DEVICE_INTEGRITY: {
    check: checkDeviceLockdown,
    failure: () => ({
      description: `Launch ${NAMES.APP} on a secure device and retry.`,
      imgSrc: '/static/img/device-checks/insecure.png',
      title: 'Rooted device detected',
    }),
  },
  REQUIRES_DEVICE_LOCKDOWN: {
    check: checkDeviceLockdown,
    failure: () => ({
      description: `Launch ${NAMES.APP} in secure mode and retry.`,
      imgSrc: '/static/img/device-checks/requires-device-lockdown.png',
      title: 'Device NOT in secure mode',
    }),
  },
  REQUIRES_SECURE_NETWORK: {
    check: checkSecureNetwork,
    failure: () => ({
      description:
        'Unable to establish a secure connection. Check your internet connection and retry. If the issue persists, contact ACT at 319-337-1510 or testact@act.org.',
      imgSrc: '/static/img/device-checks/insecure.png',
      title: 'Insecure connection',
    }),
  },
  /* TODO: Why is the touchscreen check not supported in the API?
  TOUCHSCREEN_PROHIBITED: {
    check: async () =>
      Promise.resolve({
        result: DeviceCheckResult.Pass,
      }),
    failure: () => ({
      description: 'Using touchscreen is not allowed. Contact the room proctor to determine how to proceed.',
      imgSrc: '/static/img/device-checks/touchscreen.png',
      title: 'Touch gesture detected',
    }),
  },
  */
};

export default DEVICE_CHECKS;
